import { useCallback, useMemo, useState } from 'react'

import { type PostsQuery, type SocialPlatform } from '../model/contracts'
import { usePostsFeed } from './usePostsFeed'
import { useSentimentFilter } from './useSentimentFilter'
import { useKeywordFilter } from './useKeywordFilter'
import { useFeedSort } from './useFeedSort'

export function usePostsDashboardController() {
  const [platform, setPlatform] = useState<SocialPlatform | 'all'>('all')
  const [search, setSearch] = useState('')
  const [startDate, setStartDate] = useState<string | undefined>()
  const [endDate, setEndDate] = useState<string | undefined>()

  const { sentimentFilter, setSentimentFilter } = useSentimentFilter()
  const { keywordFilter, setKeywordFilter } = useKeywordFilter()
  const { feedSort, setFeedSort } = useFeedSort()

  const query: PostsQuery = useMemo(
    () => ({
      platform: platform === 'all' ? undefined : platform,
      search: search || undefined,
      startDate,
      endDate,
      sentiment: sentimentFilter.length > 0 ? sentimentFilter : undefined,
      keywords: keywordFilter.length > 0 ? keywordFilter : undefined,
      sort: feedSort,
    }),
    [platform, search, startDate, endDate, sentimentFilter, keywordFilter, feedSort],
  )

  const { viewModel, fetchNextPage, refetch } = usePostsFeed(query)
  const feed = viewModel

  const onPlatformChange = useCallback(
    (next: SocialPlatform | 'all') => setPlatform(next),
    [],
  )

  const onSearchChange = useCallback((next: string) => setSearch(next), [])

  const onDateRangeChange = useCallback((start?: string, end?: string) => {
    setStartDate(start)
    setEndDate(end)
  }, [])

  const loadMore = useCallback(() => fetchNextPage(), [fetchNextPage])
  const retry = useCallback(() => refetch(), [refetch])

  return {
    platform,
    onPlatformChange,
    search,
    onSearchChange,
    startDate,
    endDate,
    onDateRangeChange,
    sentimentFilter,
    onSentimentFilterChange: setSentimentFilter,
    keywordFilter,
    onKeywordFilterChange: setKeywordFilter,
    feedSort,
    onFeedSortChange: setFeedSort,
    feed,
    loadMore,
    retry,
  }
}
